import React, { useEffect, useRef, useState } from "react";

import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Checkbox, ModalBase, Text } from "@performyard/py-components";

export type StatusConfirmationModalConfirmOptions = {
	removeFromCalendar?: boolean;
	removeVideoConferencing?: boolean;
};

type StatusConfirmationModalProps = {
	isOpen: boolean;
	isLoading: boolean;
	status: MeetingStatus | null;
	action?: "status-change" | "remove-from-calendar" | "remove-video-conferencing";
	onClose: () => void;
	onConfirm: (options: StatusConfirmationModalConfirmOptions) => void;
	hasConnectedCalendarEvent?: boolean;
	hasConnectedVideoConferencing?: boolean;
	meetingName?: string;
};

type ActionConfig = {
	title: string;
	message: string;
	confirmText: string;
	confirmButtonColor: "primary" | "red";
};

const getActionConfig = (
	action: "status-change" | "remove-from-calendar" | "remove-video-conferencing",
	status: MeetingStatus | null
): ActionConfig | null => {
	if (action === "remove-from-calendar") {
		return {
			title: "Remove Meeting From Calendar?",
			message:
				"Do you want to remove this meeting from your calendar? This will delete the event, but you can always add a new one later.",
			confirmText: "Yes, Remove",
			confirmButtonColor: "red",
		};
	}

	if (action === "remove-video-conferencing") {
		return {
			title: "Remove Video Conferencing?",
			message:
				"Do you want to remove the video conferencing link from this meeting? This will stop automatic transcript retrieval, but you can add the link back at any time.",
			confirmText: "Yes, Remove",
			confirmButtonColor: "red",
		};
	}

	switch (status) {
		case "canceled":
			return {
				title: "Cancel Meeting?",
				message:
					"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
				confirmText: "Yes, Cancel Meeting",
				confirmButtonColor: "primary",
			};
		case "completed":
			return {
				title: "Complete Meeting?",
				message: "Are you sure you want to complete this meeting?",
				confirmText: "Yes, Complete Meeting",
				confirmButtonColor: "primary",
			};
		case "archived":
			return {
				title: "Archive Meeting?",
				message:
					"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
				confirmText: "Yes, Archive Meeting",
				confirmButtonColor: "primary",
			};
		default:
			return null;
	}
};

export const StatusConfirmationModal = ({
	isOpen,
	isLoading,
	status,
	action = "status-change",
	onClose,
	onConfirm,
	hasConnectedCalendarEvent = false,
	hasConnectedVideoConferencing = false,
	meetingName,
}: StatusConfirmationModalProps): React.ReactElement => {
	const [removeFromCalendar, setRemoveFromCalendar] = useState(false);
	const [removeVideoConferencing, setRemoveVideoConferencing] = useState(false);

	const contentRef = useRef<{
		config: ActionConfig | null;
		showCalendarCheckbox: boolean;
		showVideoConferencingCheckbox: boolean;
		meetingName: string | undefined;
	}>({
		config: null,
		showCalendarCheckbox: false,
		showVideoConferencingCheckbox: false,
		meetingName: undefined,
	});

	useEffect(() => {
		if (isOpen) {
			const config = getActionConfig(action, status);
			const isCancelStatusChange = action === "status-change" && status === "canceled";
			contentRef.current = {
				config,
				showCalendarCheckbox: isCancelStatusChange && hasConnectedCalendarEvent,
				showVideoConferencingCheckbox: isCancelStatusChange && hasConnectedVideoConferencing,
				meetingName,
			};
		}
	}, [isOpen, action, status, hasConnectedCalendarEvent, hasConnectedVideoConferencing, meetingName]);

	const config = isOpen ? getActionConfig(action, status) : contentRef.current.config;
	const isCancelStatusChange = action === "status-change" && status === "canceled";
	const showCalendarCheckbox = isOpen
		? isCancelStatusChange && hasConnectedCalendarEvent
		: contentRef.current.showCalendarCheckbox;
	const showVideoConferencingCheckbox = isOpen
		? isCancelStatusChange && hasConnectedVideoConferencing
		: contentRef.current.showVideoConferencingCheckbox;
	const displayMeetingName = isOpen ? meetingName : contentRef.current.meetingName;

	const handleConfirm = (): void => {
		onConfirm({ removeFromCalendar, removeVideoConferencing });
	};

	const handleClose = (): void => {
		setRemoveFromCalendar(false);
		setRemoveVideoConferencing(false);
		onClose();
	};

	return (
		<ModalBase isOpen={isOpen} onClose={isLoading ? () => {} : handleClose} dangerouslySetClassName="w-[432px]">
			<div className="flex flex-col items-center gap-4 px-6 pb-6 text-center">
				<div className="flex flex-col items-center">
					<Text size="sub-heading" weight="semi-bold">
						{config?.title}
					</Text>
					{displayMeetingName && (
						<Text size="paragraph-2" weight="regular" color="neutral-6">
							{displayMeetingName}
						</Text>
					)}
					<Text size="paragraph-2" weight="regular">
						{config?.message}
					</Text>
				</div>
				{(showCalendarCheckbox || showVideoConferencingCheckbox) && (
					<div className="flex flex-col items-center gap-2">
						{showCalendarCheckbox && (
							<div className="flex items-center gap-2">
								<Checkbox
									id="remove-from-calendar"
									checked={removeFromCalendar}
									onCheckedChange={setRemoveFromCalendar}
									disabled={isLoading}
									aria-label="Remove from My Calendar"
								/>
								<Text size="paragraph-3" weight="regular">
									Remove from My Calendar
								</Text>
							</div>
						)}
						{showVideoConferencingCheckbox && (
							<div className="flex items-center gap-2">
								<Checkbox
									id="remove-video-conferencing"
									checked={removeVideoConferencing}
									onCheckedChange={setRemoveVideoConferencing}
									disabled={isLoading}
									aria-label="Remove Video Conferencing"
								/>
								<Text size="paragraph-3" weight="regular">
									Remove Video Conferencing
								</Text>
							</div>
						)}
					</div>
				)}
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={handleClose}
						disabled={isLoading}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="solid"
						color={config?.confirmButtonColor}
						size="large"
						onClick={handleConfirm}
						disabled={isLoading}
					>
						{config?.confirmText}
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};
